/**
 * raidDaySchedule — which Mega raid bosses are on TODAY, by the local clock,
 * during an event that rotates them day by day.
 *
 * LeekDuck's raid-bosses page (the roster feed) lags a daily rotation: on
 * 2026-09-04 it still listed Mega Starmie, Thursday's boss, while Friday's
 * were Mega Raichu X and Y (owner device report). An event page states the
 * rotation, so it is curated here per event, local calendar days, the same
 * auditable shape as the habitats (sources + verifiedAt).
 *
 * `applyRaidDaySchedule` rewrites the roster's Mega tier while a schedule
 * covers the local day: the day's bosses lead, the event's all-event Megas
 * (Latias / Latios) stay, and a roster Mega the schedule does not name for
 * today is dropped as stale. Every other tier is untouched. Both the Profile
 * raid panel and the Hundo Raid CPs picker read the roster through it, so
 * they can never disagree.
 *
 * Pure: no store, no DOM.
 */
import type { RaidBossDto } from "./raidRoster";
import { megaNamePrefix } from "./megaLevels";
import { speciesName } from "@/data/speciesNames";

export interface ScheduledMega {
  speciesId: number;
  /** "" / "X" / "Y". */
  megaForm: string;
}

export interface RaidDay {
  /** Local calendar day, YYYY-MM-DD. */
  day: string;
  megas: ScheduledMega[];
}

export interface RaidDaySchedule {
  eventId: string;
  eventName: string;
  /** Megas on every day of the event, alongside the day's own. */
  throughout: ScheduledMega[];
  days: RaidDay[];
  sources: readonly string[];
  verifiedAt: string;
}

const mega = (speciesId: number, megaForm = ""): ScheduledMega => ({ speciesId, megaForm });

/** Mega Ascension, 2026-08-31 to 2026-09-04: one featured Mega a day. */
export const MEGA_ASCENSION_RAIDS: RaidDaySchedule = {
  eventId: "mega-ascension",
  eventName: "Mega Ascension",
  throughout: [mega(380), mega(381)],
  days: [
    { day: "2026-08-31", megas: [mega(71), mega(149), mega(687)] },
    { day: "2026-09-01", megas: [mega(870)] },
    { day: "2026-09-02", megas: [mega(227)] },
    { day: "2026-09-03", megas: [mega(121)] },
    { day: "2026-09-04", megas: [mega(26, "X"), mega(26, "Y")] },
  ],
  sources: ["https://leekduck.com/events/mega-ascension/"],
  verifiedAt: "2026-09-04",
};

/** The habitat Megas the Mega Finale rotates through on both days. */
const FINALE_HABITAT_MEGAS: ScheduledMega[] = [
  mega(15), mega(71), mega(127), mega(460),
  mega(65), mega(80), mega(121), mega(308),
  mega(94), mega(229), mega(354), mega(687),
  mega(26, "X"), mega(26, "Y"), mega(181), mega(310),
  mega(208), mega(227), mega(306), mega(362),
  mega(319), mega(323), mega(428), mega(870),
  mega(130), mega(142), mega(149), mega(334),
  mega(302), mega(303), mega(531),
];

/** GO Fest 2026: Mega Finale. Super Mega Mewtwo X on Saturday, Y on Sunday. */
export const MEGA_FINALE_RAIDS: RaidDaySchedule = {
  eventId: "pokemon-go-fest-2026-mega-finale",
  eventName: "Pokémon GO Fest 2026: Mega Finale",
  throughout: FINALE_HABITAT_MEGAS,
  days: [
    { day: "2026-09-05", megas: [mega(150, "X")] },
    { day: "2026-09-06", megas: [mega(150, "Y")] },
  ],
  sources: ["https://leekduck.com/events/pokemon-go-fest-2026-mega-finale/"],
  verifiedAt: "2026-09-04",
};

const SCHEDULES: readonly RaidDaySchedule[] = [MEGA_ASCENSION_RAIDS, MEGA_FINALE_RAIDS];

function localDayKey(now: number): string {
  const d = new Date(now);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The schedule covering the local day, with that day's bosses, or null. */
export function raidDayAt(
  now: number = Date.now(),
): { schedule: RaidDaySchedule; day: RaidDay } | null {
  const key = localDayKey(now);
  for (const schedule of SCHEDULES) {
    const day = schedule.days.find((d) => d.day === key);
    if (day) return { schedule, day };
  }
  return null;
}

function megaName(m: ScheduledMega): string {
  return `${megaNamePrefix(m.speciesId)} ${speciesName(m.speciesId)}${m.megaForm ? ` ${m.megaForm}` : ""}`;
}

function megaKey(speciesId: number, megaForm: string): string {
  return `${speciesId}|${megaForm}`;
}

function megaFormFromName(name: string): string {
  const m = /\b([XY])$/.exec(name.trim());
  return m ? m[1] : "";
}

/**
 * The roster's tiers with the Mega tier rewritten for the local day when a
 * schedule covers it: the day's bosses first (feed rows reused where the
 * feed has them, since those carry LeekDuck's 100% CP), then the event's
 * all-event Megas, and nothing else. Returns the input untouched on a day
 * no schedule covers.
 */
export function applyRaidDaySchedule<T extends Partial<Record<string, RaidBossDto[]>>>(
  tiers: T,
  now: number = Date.now(),
): T {
  const at = raidDayAt(now);
  if (!at) return tiers;
  const feedMegas = tiers.mega ?? [];
  const feedByKey = new Map<string, RaidBossDto>();
  for (const b of feedMegas) {
    if (typeof b.speciesId === "number") feedByKey.set(megaKey(b.speciesId, megaFormFromName(b.name)), b);
  }
  const wanted = [...at.day.megas, ...at.schedule.throughout];
  const seen = new Set<string>();
  const megaTier: RaidBossDto[] = [];
  for (const m of wanted) {
    const key = megaKey(m.speciesId, m.megaForm);
    if (seen.has(key)) continue;
    seen.add(key);
    const fromFeed = feedByKey.get(key);
    megaTier.push(
      fromFeed ?? {
        name: megaName(m),
        speciesId: m.speciesId,
        tier: "mega",
        tierLabel: "Mega",
        canBeShiny: true,
        isShadow: false,
        isMega: true,
      },
    );
  }
  return { ...tiers, mega: megaTier };
}

/** A label for the picker's Mega group while a day schedule is live. */
export function raidDayGroupLabel(now: number = Date.now()): string | null {
  const at = raidDayAt(now);
  return at ? `Mega raids today · ${at.schedule.eventName}` : null;
}
